import React, {
  MouseEvent,
  ChangeEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import "./FileField.scss";

import classNames from "classnames";
import { RemoveModal } from "src/app/modals/RemoveModal/RemoveModal";
import Attachment from "../../Task/Attachments/Attachment";

import { MetaFileData } from "../MultipleFileField/MultipleFileField";

import { ReactComponent as PlusIcon } from "../../../images/plus-transparent.svg";

import { ReactComponent as CrossIcon } from "../../../images/cross.svg";

import { AttachmentPlacement, TaskType } from "../../../types";
import { getExtensionFromName, openUrlInNewTab } from "../../../utils/methods";
import { FilesDropdownPortal } from "./FilesDropdown";
import { getFileFieldIcon } from "./utils";

interface FileFieldProps {
  filesData: MetaFileData[];
  isLoading: boolean;
  handleUploadFile: (e: ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleDeleteFile: (assetId: string) => Promise<void>;
  readOnly?: boolean;
  multiple?: boolean;
}

export default function FileField({
  filesData,
  isLoading,
  handleUploadFile,
  handleDeleteFile,
  readOnly,
  multiple = true,
}: FileFieldProps) {
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [fieldWidth, setFieldWidth] = useState(0);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{
    assetId: string;
    assetName: string;
  }>({ assetId: "", assetName: "" });

  const refreshFieldWidth = () => {
    if (fieldRef?.current) {
      setFieldWidth(fieldRef.current?.offsetWidth);
    }
  };

  useEffect(() => {
    refreshFieldWidth();
  }, [filesData]);

  //
  // DOM
  //

  if (!filesData?.length) {
    return readOnly ? (
      <span>-</span>
    ) : (
      <div className="ws-table__empty-field ws-table__empty-field--file">
        <PlusIcon />
        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          onChange={handleUploadFile}
        />
      </div>
    );
  }

  const fileExtension = getExtensionFromName(filesData[0].name);

  const getPreviewFileType = (data: MetaFileData) => {
    switch (fileExtension) {
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "mp4":
      case "webm":
      case "mov":
      case "avi":
        return (
          <Attachment
            data={data}
            place={AttachmentPlacement.WsTable}
            taskType={TaskType.Creator}
          />
        );
      default:
        return getFileFieldIcon(fileExtension);
    }
  };
  const onFileClick = (event: MouseEvent) => {
    switch (fileExtension) {
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "mp4":
      case "webm":
      case "mov":
      case "avi":
        event.stopPropagation();
        break;
      default:
        openUrlInNewTab(filesData[0].publicPreviewUrl);
        break;
    }
  };

  return (
    <div
      ref={fieldRef}
      className={classNames("file-field", {
        "file-field--one-element": filesData.length === 1,
      })}
    >
      {filesData.length === 1 ? (
        <div className="file-field__value-wrapper">
          <div
            className="file-field__preview-wrapper"
            onClick={(e) => onFileClick(e)}
          >
            {getPreviewFileType(filesData[0])}
          </div>
          <div className="file-field__value">
            <a
              href={filesData[0].publicDownloadUrl}
              download
              onClick={(e) =>
                !filesData[0].publicDownloadUrl && e.preventDefault()
              }
              className="file-field__value-title"
            >
              {filesData[0].name}
            </a>
            {!readOnly && !isLoading && (
              <div
                className="file-field__buttons"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="file-field__buttons-upload">
                  <PlusIcon />
                  <input
                    ref={inputRef}
                    type="file"
                    multiple={multiple}
                    onChange={handleUploadFile}
                  />
                </span>
                <span
                  className="file-field__buttons-delete"
                  onClick={() => {
                    setShowConfirmationModal(true);
                    setFileToDelete({
                      assetId: String(filesData[0].assetId),
                      assetName: filesData[0].name,
                    });
                  }}
                >
                  <CrossIcon />
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="file-field__value">
          <FilesDropdownPortal
            filesData={filesData}
            deleteFile={handleDeleteFile}
            uploadFile={handleUploadFile}
            readOnly={readOnly}
          />
          {!readOnly && !isLoading && (
            <div
              className="file-field__buttons"
              style={{ left: `${fieldWidth}px` }}
            >
              <span className="file-field__buttons-upload">
                <PlusIcon />
                <input
                  ref={inputRef}
                  type="file"
                  multiple={multiple}
                  onChange={handleUploadFile}
                />
              </span>
            </div>
          )}
        </div>
      )}
      {isLoading && (
        <div className="file-field__loader">
          <span className="file-field__loader-progress" />
        </div>
      )}

      {showConfirmationModal && (
        <RemoveModal
          onClose={() => setShowConfirmationModal(false)}
          objectNames={[fileToDelete.assetName ?? ""]}
          removeFunction={() => {
            setShowConfirmationModal(false);
            handleDeleteFile(fileToDelete.assetId);
          }}
        />
      )}
    </div>
  );
}
